'use client';

/**
 * Offer Form Component
 * 
 * Allows customers to submit offers on listings.
 */

import { useState, useMemo, useCallback, type FormEvent } from 'react';
import { useRouter } from 'next/navigation';
import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import { useOffers } from '@/hooks/useOffers';
import { 
  Loader2, 
  CheckCircle2, 
  AlertCircle, 
  Calendar,
  FileText,
  ChevronLeft,
  ChevronRight,
  Check,
} from 'lucide-react';
import type { OfferCondition, CreateOfferRequest } from '@/types/customer';
import { CURRENCY_CONFIG, type Currency } from '@/lib/config/app.config';

// ============================================================================
// Types
// ============================================================================

export interface OfferFormProps {
  /** Listing ID to make offer on */
  listingId: string;
  /** Listing title for display */
  listingTitle?: string;
  /** Current asking price for reference */
  askingPrice?: number;
  /** Currency of the asking price */
  askingCurrency?: string;
  /** Custom class name */
  className?: string;
  /** Login URL for unauthenticated users */
  loginUrl?: string;
  /** Callback after successful submission */
  onSuccess?: (offerId: string) => void;
  /** Callback to close modal/form */
  onClose?: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const OFFER_CONDITIONS: { value: OfferCondition; label: string; description: string }[] = [
  { value: 'CASH_BUYER', label: 'Cash Buyer', description: 'No mortgage required' },
  { value: 'FIRST_TIME_BUYER', label: 'First Time Buyer', description: 'Purchasing first property' },
  { value: 'CHAIN_FREE', label: 'Chain Free', description: 'No property to sell' },
  { value: 'SUBJECT_TO_FINANCE', label: 'Subject to Finance', description: 'Conditional on mortgage approval' },
  { value: 'SUBJECT_TO_SURVEY', label: 'Subject to Survey', description: 'Conditional on satisfactory survey' },
  { value: 'SUBJECT_TO_VALUATION', label: 'Subject to Valuation', description: 'Conditional on bank valuation' },
  { value: 'SUBJECT_TO_INSPECTION', label: 'Subject to Inspection', description: 'Conditional on property inspection' },
  { value: 'SUBJECT_TO_SALE', label: 'Subject to Sale', description: 'Conditional on selling existing property' },
];

// ============================================================================
// Calendar helpers
// ============================================================================

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const generateCalendarDays = (year: number, month: number) => {
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // getDay() returns 0=Sun, we want Mon=0
  const startingDayOfWeek = (firstDay.getDay() + 6) % 7;

  const days: (number | null)[] = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }
  while (days.length % 7 !== 0) {
    days.push(null);
  }

  return days;
};

const formatDateDMY = (date: Date): string => {
  const d = date.getDate().toString().padStart(2, '0');
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
};

const dateToISOString = (date: Date): string => {
  const y = date.getFullYear();
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const d = date.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// ============================================================================
// Inline Calendar Picker
// ============================================================================

function InlineCalendarPicker({
  value,
  onChange,
  minDate,
  label,
}: {
  value: string;
  onChange: (isoDate: string) => void;
  minDate?: Date;
  label: string;
}) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const min = minDate ?? today;

  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(
    value ? new Date(value).getMonth() : today.getMonth()
  );
  const [currentYear, setCurrentYear] = useState(
    value ? new Date(value).getFullYear() : today.getFullYear()
  );

  const calendarDays = useMemo(
    () => generateCalendarDays(currentYear, currentMonth),
    [currentYear, currentMonth]
  );

  const selectedDate = value ? new Date(value) : null;

  const isDateDisabled = useCallback((day: number) => {
    const date = new Date(currentYear, currentMonth, day);
    return date < min;
  }, [currentYear, currentMonth, min]);

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((prev) => prev - 1);
    } else {
      setCurrentMonth((prev) => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((prev) => prev + 1);
    } else {
      setCurrentMonth((prev) => prev + 1);
    }
  };

  const handleDateSelect = (day: number) => {
    if (!isDateDisabled(day)) {
      const selected = new Date(currentYear, currentMonth, day);
      onChange(dateToISOString(selected));
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    onChange('');
    setIsOpen(false);
  };

  const canGoPrevMonth =
    currentYear > min.getFullYear() ||
    (currentYear === min.getFullYear() && currentMonth > min.getMonth());

  const inputClasses = 'w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-400 transition-all';

  return (
    <div className="relative">
      <div className="relative">
        <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`${inputClasses} pl-10 text-left`}
        >
          {selectedDate ? formatDateDMY(selectedDate) : (
            <span className="text-stone-400">Select {label.toLowerCase()}</span>
          )}
        </button>
      </div>

      {isOpen && (
        <div className="absolute z-20 mt-2 w-full bg-white rounded-2xl border border-stone-200 shadow-xl p-4">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={handlePrevMonth}
              disabled={!canGoPrevMonth}
              className="p-1.5 rounded-lg hover:bg-stone-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-stone-600" />
            </button>
            <h4 className="text-sm font-semibold text-stone-900">
              {MONTHS[currentMonth]} {currentYear}
            </h4>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1.5 rounded-lg hover:bg-stone-100 transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-stone-600" />
            </button>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEKDAYS.map((day) => (
              <div key={day} className="text-center text-[11px] font-medium text-stone-400 py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day, index) => {
              if (day === null) {
                return <div key={`empty-${index}`} className="aspect-square" />;
              }

              const disabled = isDateDisabled(day);
              const isSelected =
                selectedDate?.getDate() === day &&
                selectedDate?.getMonth() === currentMonth &&
                selectedDate?.getFullYear() === currentYear;
              const isToday =
                day === today.getDate() &&
                currentMonth === today.getMonth() &&
                currentYear === today.getFullYear();

              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => handleDateSelect(day)}
                  disabled={disabled}
                  className={`
                    aspect-square rounded-xl text-xs font-medium transition-all duration-200
                    ${disabled
                      ? 'text-stone-300 cursor-not-allowed'
                      : 'hover:bg-stone-100 cursor-pointer'}
                    ${isSelected
                      ? 'bg-stone-900 text-white shadow-md'
                      : isToday
                        ? 'bg-stone-100 text-stone-900 font-semibold'
                        : 'text-stone-700'}
                  `}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Clear / close actions */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone-100">
            <button
              type="button"
              onClick={handleClear}
              className="text-xs text-stone-400 hover:text-stone-600 transition-colors"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-xs font-medium text-stone-900 hover:text-stone-700 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function OfferForm({
  listingId,
  listingTitle: _listingTitle,
  askingPrice,
  askingCurrency = 'USD',
  className = '',
  loginUrl = '/login',
  onSuccess,
  onClose: _onClose,
}: OfferFormProps) {
  const router = useRouter();
  const { isAuthenticated, isLoading: authLoading } = useCustomerAuth();
  const { createOffer, submitOffer } = useOffers();

  const currencyConfig = CURRENCY_CONFIG[askingCurrency as Currency] ?? CURRENCY_CONFIG.USD;

  const [formData, setFormData] = useState({
    priceAmount: askingPrice || 0,
    priceCurrency: askingCurrency,
    depositAmount: 0,
    validUntil: '',
    proposedCompletionDate: '',
    conditions: [] as OfferCondition[],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isAuthenticated) {
      const returnUrl = typeof window !== 'undefined' ? window.location.pathname : '';
      router.push(`${loginUrl}?redirect=${encodeURIComponent(returnUrl)}`);
      return;
    }

    if (formData.priceAmount <= 0) {
      setError('Please enter a valid offer amount');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const offerData: CreateOfferRequest = {
        listingId,
        priceAmount: formData.priceAmount,
        priceCurrency: formData.priceCurrency,
        ...(formData.depositAmount > 0 ? { depositAmount: formData.depositAmount } : {}),
        ...(formData.validUntil ? { validUntil: formData.validUntil } : {}),
        ...(formData.proposedCompletionDate ? { proposedCompletionDate: formData.proposedCompletionDate } : {}),
        ...(formData.conditions.length > 0 ? { conditions: formData.conditions } : {}),
      };

      const offer = await createOffer(offerData);

      if (!offer) {
        throw new Error('Failed to create offer');
      }

      const submittedOffer = await submitOffer(offer.id);

      if (!submittedOffer) {
        throw new Error('Failed to submit offer');
      }

      setIsSuccess(true);
      onSuccess?.(submittedOffer.id);

    } catch (err) {
      console.error('[OfferForm] Failed to submit:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit offer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConditionToggle = (condition: OfferCondition) => {
    setFormData((prev) => ({
      ...prev,
      conditions: prev.conditions.includes(condition)
        ? prev.conditions.filter((c) => c !== condition)
        : [...prev.conditions, condition],
    }));
  };

  const formatCurrency = (amount: number, currency: string) => {
    const config = CURRENCY_CONFIG[currency as Currency];
    return new Intl.NumberFormat(config?.locale ?? 'en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: config?.decimalPlaces ?? 0,
      maximumFractionDigits: config?.decimalPlaces ?? 0,
    }).format(amount);
  };

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <div className={`flex items-center justify-center py-12 ${className}`}>
        <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
      </div>
    );
  }

  // Not authenticated - show login prompt
  if (!isAuthenticated) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <FileText className="h-7 w-7 text-stone-500" />
        </div>
        <h3 className="text-lg font-bold text-stone-900 mb-2">Sign in to Make an Offer</h3>
        <p className="text-stone-500 text-sm mb-6">
          Create an account or sign in to submit your offer.
        </p>
        <button
          onClick={() => {
            const returnUrl = typeof window !== 'undefined' ? window.location.pathname : '';
            router.push(`${loginUrl}?redirect=${encodeURIComponent(returnUrl)}`);
          }}
          className="px-6 py-2.5 rounded-xl bg-stone-900 text-white font-medium hover:bg-stone-800 transition-colors"
        >
          Sign In
        </button>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-lg font-bold text-stone-900 mb-2">Offer Submitted!</h3>
        <p className="text-stone-500 text-sm mb-6">
          Your offer has been sent to the agent. You&apos;ll be notified when they respond.
        </p>
        <button
          onClick={() => router.push('/account/offers')}
          className="px-6 py-2.5 rounded-xl bg-stone-900 text-white font-medium hover:bg-stone-800 transition-colors"
        >
          View My Offers
        </button>
      </div>
    );
  }

  const inputClasses = 'w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-400 transition-all';
  const labelClasses = 'block text-sm font-medium text-stone-700 mb-2';

  return (
    <form onSubmit={handleSubmit} className={className}>
      {/* Asking Price Reference */}
      {askingPrice && (
        <div className="mb-6 p-4 rounded-2xl bg-stone-50 border border-stone-100">
          <div className="text-xs text-stone-500 uppercase tracking-wider font-medium">Asking Price</div>
          <div className="text-2xl font-bold text-stone-900 mt-1">
            {formatCurrency(askingPrice, askingCurrency)}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 rounded-2xl bg-red-50 border border-red-100 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Unable to submit</p>
            <p className="text-xs text-red-600 mt-0.5">{error}</p>
          </div>
        </div>
      )}

      <div className="space-y-5">
        {/* Offer Amount */}
        <div>
          <label htmlFor="priceAmount" className={labelClasses}>
            Your Offer <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-medium text-stone-400 pointer-events-none">
              {currencyConfig.symbol}
            </span>
            <input
              type="number"
              id="priceAmount"
              value={formData.priceAmount || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, priceAmount: Number(e.target.value) }))}
              placeholder="Enter your offer amount"
              min={0}
              required
              className={`${inputClasses} pl-10`}
            />
            <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-medium text-stone-400 pointer-events-none">
              {askingCurrency}
            </span>
          </div>
          {askingPrice && formData.priceAmount > 0 && (
            <p className="mt-1.5 text-xs text-stone-500">
              {formData.priceAmount < askingPrice
                ? `${((1 - formData.priceAmount / askingPrice) * 100).toFixed(1)}% below asking`
                : formData.priceAmount > askingPrice
                ? `${(((formData.priceAmount / askingPrice) - 1) * 100).toFixed(1)}% above asking`
                : 'At asking price'}
            </p>
          )}
        </div>

        {/* Deposit Amount */}
        <div>
          <label htmlFor="depositAmount" className={labelClasses}>
            Deposit Amount <span className="text-stone-400 font-normal">(Optional)</span>
          </label>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-medium text-stone-400 pointer-events-none">
              {currencyConfig.symbol}
            </span>
            <input
              type="number"
              id="depositAmount"
              value={formData.depositAmount || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, depositAmount: Number(e.target.value) }))}
              placeholder="Deposit you're prepared to pay"
              min={0}
              className={`${inputClasses} pl-10`}
            />
            <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-medium text-stone-400 pointer-events-none">
              {askingCurrency}
            </span>
          </div>
        </div>

        {/* Proposed Completion Date */}
        <div>
          <label className={labelClasses}>
            Proposed Completion Date <span className="text-stone-400 font-normal">(Optional)</span>
          </label>
          <InlineCalendarPicker
            value={formData.proposedCompletionDate}
            onChange={(isoDate) => setFormData((prev) => ({ ...prev, proposedCompletionDate: isoDate }))}
            label="completion date"
          />
        </div>

        {/* Offer Valid Until */}
        <div>
          <label className={labelClasses}>
            Offer Valid Until <span className="text-stone-400 font-normal">(Optional)</span>
          </label>
          <InlineCalendarPicker
            value={formData.validUntil}
            onChange={(isoDate) => setFormData((prev) => ({ ...prev, validUntil: isoDate }))}
            label="expiry date"
          />
        </div>

        {/* Conditions */}
        <div>
          <label className={labelClasses}>Buyer Status & Conditions</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-2">
            {OFFER_CONDITIONS.map((condition) => {
              const isChecked = formData.conditions.includes(condition.value);
              return (
                <label
                  key={condition.value}
                  className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                    isChecked
                      ? 'border-stone-900 bg-stone-50 ring-1 ring-stone-900'
                      : 'border-stone-200 hover:border-stone-300 hover:bg-stone-50/50'
                  }`}
                >
                  <div className="relative flex items-center mt-0.5">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleConditionToggle(condition.value)}
                      className="peer appearance-none h-5 w-5 border-2 border-stone-300 rounded-md checked:bg-stone-900 checked:border-stone-900 transition-colors cursor-pointer"
                    />
                    <Check
                      size={12}
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-stone-900">{condition.label}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{condition.description}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="mt-8">
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-3.5 px-4 rounded-xl bg-stone-900 text-white font-semibold hover:bg-stone-800 focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Submitting Offer...
            </>
          ) : (
            'Submit Offer'
          )}
        </button>
      </div>

      {/* Disclaimer */}
      <p className="mt-4 text-[11px] text-stone-400 text-center leading-relaxed">
        By submitting an offer, you agree to our terms and conditions. This is not a legally binding contract.
      </p>
    </form>
  );
}

export default OfferForm;
